import { createForm } from '@seek/forms-ui';
import { getZoneFromSite } from '@seek/melways-sites';
import React from 'react';

import {
  Role,
  type UserDetails,
  type HirerUserEmailFoundPayloadV2,
  UserStatus,
} from 'src/graphql/generated';
import { useConfig } from 'src/modules/ConfigProvider';

import { FormRoot } from './Components';
import type { UserFormValues } from './userFormFields';

export enum FlowKey {
  Add = 'add',
  Edit = 'edit',
}

const { FormProvider, useField } = createForm<UserFormValues>();
export { useField };

interface UserFormProviderProps {
  flowKey: FlowKey;
  providedEmail?: string;
  userExistingOnAdd?: HirerUserEmailFoundPayloadV2;
  userToEdit?: UserDetails;
}

export const UserFormProvider = ({
  flowKey,
  providedEmail,
  userExistingOnAdd,
  userToEdit,
}: UserFormProviderProps) => {
  const isAddFlow = Boolean(flowKey === FlowKey.Add);
  const isEditFlow = !isAddFlow;
  const { site, locale } = useConfig();

  const initialValues = isAddFlow
    ? {
        firstName: '',
        lastName: '',
        email: providedEmail ?? '',
        role: Role.USER,
        status: userExistingOnAdd?.status ?? UserStatus.PENDING,
        canCreatePosition: true,
        hasAllPositionAccess: true,
        hasAnalytics: false,
        hasPremiumTalentSearch: false,
        originZone: getZoneFromSite(site),
      }
    : {
        firstName: userToEdit?.firstName ?? '',
        lastName: userToEdit?.lastName ?? '',
        email: userToEdit?.email ?? '',
        role: userToEdit?.permissions?.role.toUpperCase() as Role,
        status: userToEdit?.status ?? UserStatus.PENDING,
        canCreatePosition: Boolean(userToEdit?.permissions?.canCreatePosition),
        hasAllPositionAccess: Boolean(
          userToEdit?.permissions?.hasAllPositionAccess,
        ),
        hasAnalytics: Boolean(userToEdit?.permissions?.hasAnalytics),
        hasPremiumTalentSearch: Boolean(
          userToEdit?.permissions?.hasPremiumTalentSearch,
        ),
        originZone: getZoneFromSite(site),
      };

  const email = isAddFlow ? providedEmail : userToEdit?.email;

  return (
    <FormProvider initialValues={initialValues} language={locale}>
      {(formProps) => (
        <FormRoot
          isAddFlow={isAddFlow}
          isEditFlow={isEditFlow}
          email={email ?? ''}
          {...(userExistingOnAdd && { userExistingOnAdd })}
          {...(userToEdit && { userToEdit })}
          {...formProps}
        />
      )}
    </FormProvider>
  );
};
